import { withoutTrailingSlash } from 'ufo'

import { jsonLdScript, organizationJsonLd, websiteJsonLd } from '../utils/jsonLd'

/**
 * Глобальная SEO-база (SKUBY-SEO-MODULE-SPEC.md §5–6): дефолтный title витрины,
 * шаблон «{Страница} — {Витрина}», noindex некоммерческих разделов,
 * дефолты Open Graph / Twitter Card (страницы переопределяют точечно) и
 * JSON-LD Organization + WebSite (+SearchAction) на главной.
 * Универсальный (не .client) — мета обязана попадать в SSR-HTML для краулера.
 */

// Некоммерческие разделы (ЛК, заказы, поиск) не индексируем — единое правило
// по префиксу пути, чтобы не дублировать его в каждой странице модулей
const NOINDEX_PREFIXES = ['/profile', '/order', '/search']

// Запасная og-картинка витрины (PWA-иконка) — если в настройках канала
// не задана DefaultOgImage (ChannelWebSeoConfig)
const FALLBACK_OG_IMAGE_PATH = '/android-chrome-512x512.png'

export default defineNuxtPlugin({
  name: 'seoRoot:plugin',
  /**
   * Порядок обязателен: seo-config-loader должен успеть загрузить настройки
   * канала до сборки меты. На сервере watchEffect отрабатывает один раз
   * синхронно — реактивного пересчёта после SSR не будет, и дефолтная
   * og-картинка «застряла» бы на fallback. Заодно гарантирует поднятый
   * DI-контейнер (loader зависит от alt-container).
   */
  dependsOn: ['seo-config-loader:plugin'],

  setup() {
    const siteName = useSiteName()
    const { service: seo } = useSeo()
    const route = useRoute()
    const url = useRequestURL()
    // Пагинированный листинг канонизирует сам себя (страница 2+ ≠ дубль
    // первой): суффикс ?page&afterField&afterId, на прочих страницах — ''
    const { canonicalQuerySuffix, isPaginated } = useListingPagination()

    const canonicalUrl = () =>
      url.origin
      + withoutTrailingSlash(route.path)
      + canonicalQuerySuffix.value

    // Считаем жадно в setup: геттеры head выполняются вне setup-контекста, а
    // image-провайдер внутри зовёт useRuntimeConfig и там падает (см. !37)
    const defaultOgImage = ref<string>()
    watchEffect(() => {
      defaultOgImage.value
        = seo.defaultOgImage() ?? url.origin + FALLBACK_OG_IMAGE_PATH
    })

    // Картинки товаров отдаёт API-хост (провайдер skubyApi) — preconnect
    // снимает DNS+TLS с критического пути первой картинки (LCP)
    const runtimeConfig = useRuntimeConfig()
    const apiOrigin = (() => {
      try {
        const base = String(runtimeConfig.public.API_BASE_URL ?? '')
        return base && new URL(base).origin !== url.origin
          ? new URL(base).origin
          : undefined
      } catch {
        return undefined
      }
    })()

    if (apiOrigin) {
      useHead({
        link: [
          { rel: 'preconnect', href: apiOrigin, crossorigin: '' },
          { rel: 'dns-prefetch', href: apiOrigin },
        ],
      })
    }

    useHead({
      titleTemplate: title =>
        title ? `${title} — ${siteName.value}` : siteName.value,

      // canonical: origin + путь без trailing slash и query (?category и
      // т.п.); исключение — параметры пагинации листинга (см. выше).
      // Для PDP корректность пути гарантирует 301 из useProductSlugRedirect
      link: [
        {
          key: 'canonical',
          rel: 'canonical',
          href: canonicalUrl,
        },
      ],
    })

    useSeoMeta({
      // Query listings at bare `/catalog?q=` are non-indexable (R5 unify).
      // Exact path + query — never noindex `/catalog/:slug` hubs/PLPs or root.
      robots: () => {
        // Trailing-slash forms (`/catalog/`) must match bare catalog + `q`
        // (Nitro may normalize `/catalog?q=` → `/catalog/?q=`).
        if (withoutTrailingSlash(route.path) === '/catalog' && 'q' in route.query) {
          return 'noindex, follow'
        }

        return NOINDEX_PREFIXES.some(prefix => route.path.startsWith(prefix))
          ? 'noindex, follow'
          : 'index, follow'
      },

      // Open Graph / Twitter: дефолты уровня витрины; og:url совпадает
      // с canonical
      ogSiteName: () => siteName.value,
      ogType: 'website',
      ogLocale: 'ru_RU',
      ogUrl: canonicalUrl,
      ogImage: () => defaultOgImage.value,
      twitterCard: 'summary',
    })

    // JSON-LD Organization + WebSite — только на главной (рекомендация Google
    // для sitelinks searchbox). Собираем внутри computed, а не в setup:
    // siteName зависит от hostLoader-плагина, порядок которого не гарантирован
    useHead(computed(() => ({
      // только настоящая главная — не её пагинированные слайсы (/?page=2)
      script: route.path === '/' && !isPaginated.value
        ? [
            jsonLdScript(organizationJsonLd({
              siteName: siteName.value,
              origin: url.origin,
              logo: defaultOgImage.value,
            })),
            jsonLdScript(websiteJsonLd({
              siteName: siteName.value,
              origin: url.origin,
            })),
          ]
        : [],
    })))
  },
})
